import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import { formatCurrency } from '../../utils/displayHelpers';
import { getDirectShopCataloguePath } from '../../utils/shopUrls';
import './AdvancedSearch.css';

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ services: [], professionals: [], shops: [] });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(!isMobile);

  // Filter state
  const [filters, setFilters] = useState({
    type: 'all', // 'all', 'services', 'professionals', 'shops'
    category: 'all',
    priceMin: '',
    priceMax: '',
    rating: 0, // 0, 3, 4, 5
    availability: 'all', // 'all', 'today', 'thisWeek', 'custom'
    distance: 50, // km radius
    sortBy: 'relevance' // 'relevance', 'priceAsc', 'priceDesc', 'rating', 'distance'
  });

  // Location state (for "Near Me" feature)
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState('');

  // Categories
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      performSearch();
    }
  }, [searchQuery, filters]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/services/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
    }
  };

  const requestLocation = () => {
    if (navigator.geolocation) {
      setLocationError('');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          performSearch();
        },
        (error) => {
          setLocationError('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  const performSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setResults({ services: [], professionals: [], shops: [] });
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('q', searchQuery);
      params.append('type', filters.type);
      
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.priceMin) params.append('priceMin', filters.priceMin);
      if (filters.priceMax) params.append('priceMax', filters.priceMax);
      if (filters.rating > 0) params.append('minRating', filters.rating);
      if (filters.availability !== 'all') params.append('availability', filters.availability);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
        params.append('radius', filters.distance);
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/search?${params.toString()}`);
      
      if (res.ok) {
        const data = await res.json();
        setResults(data);
      } else {
        setResults({ services: [], professionals: [], shops: [] });
      }
    } catch (error) {
      setResults({ services: [], professionals: [], shops: [] });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, userLocation]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    performSearch();
  };

  const clearFilters = () => {
    setFilters({
      type: 'all',
      category: 'all',
      priceMin: '',
      priceMax: '',
      rating: 0,
      availability: 'all',
      distance: 50,
      sortBy: 'relevance'
    });
  };

  const totalResults = results.services.length + results.professionals.length + results.shops.length;

  return (
    <>
      <CustomerHeader />
      <div style={{ paddingTop: '80px', minHeight: '100vh', background: '#f9fafb' }}>
        
        {/* Search Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
          padding: isMobile ? '24px 16px' : '40px 24px',
          color: 'white'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h1 style={{ fontSize: isMobile ? '24px' : '32px', margin: '0 0 20px 0', fontWeight: '700' }}>
              🔍 Advanced Search
            </h1>
            
            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search services, professionals, or shops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: '250px',
                  padding: '14px 20px',
                  fontSize: '16px',
                  border: 'none',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '14px 28px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}
              >
                Search
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  padding: '14px 20px',
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}
              >
                {showFilters ? '🔽 Hide' : '⚙️ Filters'}
              </button>
            </form>
          </div>
        </div>

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '20px 16px' : '40px 24px' }}>
          
          {/* Filters Panel */}
          {showFilters && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Filters</h3>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: '6px 12px',
                    background: '#f3f4f6',
                    color: '#6b7280',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Clear All
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px'
              }}>
                
                {/* Type Filter */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Search Type
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="all">All Results</option>
                    <option value="services">Services Only</option>
                    <option value="professionals">Professionals Only</option>
                    <option value="shops">Shops Only</option>
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Price Range
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.priceMin}
                      onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.priceMax}
                      onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* Rating Filter */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Minimum Rating
                  </label>
                  <select
                    value={filters.rating}
                    onChange={(e) => setFilters({ ...filters, rating: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="0">Any Rating</option>
                    <option value="3">3+ ⭐⭐⭐</option>
                    <option value="4">4+ ⭐⭐⭐⭐</option>
                    <option value="5">5 ⭐⭐⭐⭐⭐</option>
                  </select>
                </div>

                {/* Sort By */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="relevance">Relevance</option>
                    <option value="priceAsc">Price: Low to High</option>
                    <option value="priceDesc">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    {userLocation && <option value="distance">Distance</option>}
                  </select>
                </div>

                {/* Distance (if location enabled) */}
                {userLocation && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                      Distance: {filters.distance} km
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={filters.distance}
                      onChange={(e) => setFilters({ ...filters, distance: parseInt(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
              </div>

              {/* Near Me Button */}
              {!userLocation && (
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={requestLocation}
                    style={{
                      padding: '10px 20px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    📍 Search Near Me
                  </button>
                  {locationError && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#ef4444' }}>
                      {locationError}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Results Section */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', fontSize: '16px', color: '#6b7280' }}>
              Searching...
            </div>
          ) : totalResults === 0 && searchQuery ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '60px 20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔍</div>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: '#374151' }}>
                No results found for "{searchQuery}"
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px' }}>
                Try adjusting your filters or search terms
              </p>
            </div>
          ) : totalResults > 0 ? (
            <>
              {/* Results Summary */}
              <div style={{ marginBottom: '24px', fontSize: '16px', color: '#6b7280' }}>
                Found <strong style={{ color: '#374151' }}>{totalResults}</strong> results for "{searchQuery}"
              </div>

              {/* Services */}
              {results.services.length > 0 && (
                <div style={{ marginBottom: '40px' }}>
                  <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#374151' }}>
                    Services ({results.services.length})
                  </h2>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '20px'
                  }}>
                    {results.services.map(service => (
                      <div
                        key={service._id}
                        style={{
                          background: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => navigate(`/service/${service._id}`)}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#374151' }}>
                          {service.name}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                          {service.description}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '20px', fontWeight: '700', color: '#2d6da3' }}>
                            {formatCurrency(service.price)}
                          </span>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            {service.durationMinutes} min
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Professionals */}
              {results.professionals.length > 0 && (
                <div style={{ marginBottom: '40px' }}>
                  <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#374151' }}>
                    Professionals ({results.professionals.length})
                  </h2>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '20px'
                  }}>
                    {results.professionals.map(professional => (
                      <div
                        key={professional._id}
                        style={{
                          background: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => {
                          const isIndependent = ["R4", "R7"].includes(
                            professional.role,
                          );
                          const independentBasePath =
                            professional.role === "R7"
                              ? "/freelancers"
                              : "/freelancer";

                          if (isIndependent && professional.slug) {
                            navigate(`${independentBasePath}/${professional.slug}`);
                            return;
                          }

                          if (isIndependent) {
                            navigate(`${independentBasePath}/${professional._id}`);
                            return;
                          }

                          if (professional.slug) {
                            navigate(`/professional/${professional.slug}`);
                            return;
                          }

                          navigate(`/professional/${professional._id}`);
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          background: professional.profileImage ? `url(${professional.profileImage}) center/cover` : '#e5e7eb',
                          margin: '0 auto 12px'
                        }} />
                        <h3 style={{ fontSize: '16px', marginBottom: '4px', color: '#374151' }}>
                          {professional.firstName} {professional.lastName}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b7280' }}>
                          {professional.bio || professional.specialization || 'Professional'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shops */}
              {results.shops.length > 0 && (
                <div style={{ marginBottom: '40px' }}>
                  <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#374151' }}>
                    Shops ({results.shops.length})
                  </h2>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '20px'
                  }}>
                    {results.shops.map(shop => (
                      <div
                        key={shop._id}
                        style={{
                          background: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => {
                          const shopPath = getDirectShopCataloguePath(shop);
                          navigate(shopPath || '/shops');
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                      >
                        <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#374151' }}>
                          {shop.name}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                          {shop.address || 'Shop location'}
                        </p>
                        {shop.distance && (
                          <div style={{ fontSize: '14px', color: '#10b981', fontWeight: '600' }}>
                            📍 {shop.distance.toFixed(1)} km away
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : !searchQuery ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '60px 20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔍</div>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: '#374151' }}>
                Start Your Search
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px' }}>
                Search for services, professionals, or shops near you
              </p>
            </div>
          ) : null}
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default AdvancedSearch;
